import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Custom hook que encapsula toda a lógica do RestoreModal:
 * estado do modo (upload/drive), arquivo, lista do Drive, confirmação e
 * execução do restore contra o backend. A UI é 100% dumb components.
 */
export default function useRestore({ onClose, onRestored }) {
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveFilesLoading, setDriveFilesLoading] = useState(false);
  const [selectedDriveFile, setSelectedDriveFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);

  const reset = () => {
    setMode("upload");
    setFile(null);
    setDriveFiles([]);
    setSelectedDriveFile(null);
    setConfirm("");
  };

  const handleClose = () => {
    if (restoring) return;
    onClose();
    reset();
  };

  const loadDriveFiles = async () => {
    setDriveFilesLoading(true);
    try {
      const { data } = await api.get("/backup/drive-files");
      setDriveFiles(data.files || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao listar arquivos do Drive");
    } finally { setDriveFilesLoading(false); }
  };

  const switchMode = async (nextMode) => {
    setMode(nextMode);
    setFile(null);
    setSelectedDriveFile(null);
    if (nextMode === "drive" && driveFiles.length === 0) await loadDriveFiles();
  };

  const canConfirm = () => {
    if (confirm !== "RESTAURAR") return false;
    if (mode === "upload") return !!file;
    if (mode === "drive") return !!selectedDriveFile;
    return false;
  };

  const execute = async () => {
    if (!canConfirm()) return;
    setRestoring(true);
    const toastId = toast.loading("Restaurando banco... isso pode levar alguns minutos.");
    try {
      const result = await runRestore({ mode, file, selectedDriveFile });
      notifyRestoreResult(result, toastId);
      if (result.ok) {
        onClose();
        reset();
      }
      if (onRestored) await onRestored();
      if (result.ok) setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao restaurar", { id: toastId, duration: 10000 });
    } finally {
      setRestoring(false);
    }
  };

  return {
    mode, setMode, switchMode,
    file, setFile,
    driveFiles, driveFilesLoading, selectedDriveFile, setSelectedDriveFile,
    confirm, setConfirm,
    restoring,
    canConfirm, execute, handleClose,
  };
}

function notifyRestoreResult(result, toastId) {
  if (result.ok) {
    const elapsed = ((result.elapsed_ms || 0) / 1000).toFixed(1);
    const safety = result.safety_backup ? ` · Safety: ${result.safety_backup}` : "";
    toast.success(`Banco restaurado com sucesso em ${elapsed}s${safety}`, { id: toastId, duration: 8000 });
  } else {
    toast.error(result.error || "Falha ao restaurar", { id: toastId, duration: 10000 });
  }
}

async function runRestore({ mode, file, selectedDriveFile }) {
  if (mode === "upload") {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", "RESTAURAR");
    const { data } = await api.post("/backup/restore/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  }
  const { data } = await api.post("/backup/restore/drive", {
    file_id: selectedDriveFile.id,
    confirm: "RESTAURAR",
  });
  return data;
}
